/**
 * SearchBar - Date range selection and search button
 */
import { ReactElement, useCallback } from 'react'
import { connect } from 'react-redux'
import { Flex, Button, Select, DatePicker } from 'antd'
import { SearchOutlined, CalendarOutlined } from '@ant-design/icons'
import type { Dayjs } from 'dayjs'
import dayjs from 'dayjs'
import log from 'lib/log'
import { setTimeRange, setCustomDateFilter } from 'actions/search/filters'
import { triggerSearch } from 'actions/search'
import { getTimeRange, getCustomDateRange } from 'selectors/search/filters'
import { TimeRange } from 'types/search/filters'

interface SearchBarProps {
  timeRange: TimeRange
  customDateRange: { startDate: string | null; endDate: string | null }
}

interface ReduxState {
  timeRange: TimeRange
  customDateRange: { startDate: string | null; endDate: string | null }
}

const select = (): ReduxState => ({
  timeRange: getTimeRange(),
  customDateRange: getCustomDateRange(),
})

// Time range options for the dropdown
const TIME_RANGE_OPTIONS = [
  { value: TimeRange.TEN_MIN, label: 'Last 10 minutes' },
  { value: TimeRange.THIRTY_MIN, label: 'Last 30 minutes' },
  { value: TimeRange.ONE_HOUR, label: 'Last hour' },
  { value: TimeRange.THREE_HOURS, label: 'Last 3 hours' },
  { value: TimeRange.SIX_HOURS, label: 'Last 6 hours' },
  { value: TimeRange.TWELVE_HOURS, label: 'Last 12 hours' },
  { value: TimeRange.ONE_DAY, label: 'Last day' },
  { value: TimeRange.TWO_DAYS, label: 'Last 2 days' },
  { value: TimeRange.THREE_DAYS, label: 'Last 3 days' },
  { value: TimeRange.SEVEN_DAYS, label: 'Last 7 days' },
  { value: TimeRange.CUSTOM, label: 'Custom time range' },
]

/**
 * SearchBar - Date range selection and manual search trigger
 * Connected to Redux for autonomous state management
 */
const SearchBar = (props: SearchBarProps): ReactElement => {
  const { timeRange, customDateRange } = props

  const handleTimeRangeChange = useCallback((value: TimeRange) => {
    log.debug('Time range changed to', { value })
    setTimeRange(value)
  }, [])

  const handleCustomDateRangeChange = useCallback(
    (dates: [Dayjs | null, Dayjs | null] | null) => {
      log.debug('Custom time range changed', { value: JSON.stringify(dates) })
      if (dates && dates[0]) {
        // Start date is required, end date is optional
        const startDate = dates[0].toISOString()
        const endDate = dates[1] ? dates[1].toISOString() : null
        setCustomDateFilter(startDate, endDate)
      } else {
        setCustomDateFilter(null, null)
      }
    },
    [],
  )

  const handleSearch = useCallback(() => {
    triggerSearch(true)
  }, [])

  return (
    <Flex
      justify="space-between"
      align="center"
      style={{ padding: '8px 16px', borderBottom: '1px solid #e8e8e8' }}
      data-tour="search-bar"
    >
      <Flex gap={8} align="center">
        <Select
          value={timeRange}
          onChange={handleTimeRangeChange}
          options={TIME_RANGE_OPTIONS}
          style={{ width: 200 }}
          suffixIcon={<CalendarOutlined />}
          listHeight={400}
        />

        {timeRange === TimeRange.CUSTOM && (
          <DatePicker.RangePicker
            showTime
            value={
              customDateRange.startDate
                ? [
                    dayjs(customDateRange.startDate),
                    customDateRange.endDate
                      ? dayjs(customDateRange.endDate)
                      : null,
                  ]
                : null
            }
            onChange={handleCustomDateRangeChange}
            format="YYYY-MM-DD HH:mm:ss"
            style={{ width: 400 }}
            allowEmpty={[false, true]}
            disabledDate={(current) => {
              // Disable dates in the future
              return current && current > dayjs()
            }}
            disabledTime={(current, type) => {
              // Disable future times for today
              if (!current || !current.isSame(dayjs(), 'day')) {
                return {}
              }
              const now = dayjs()
              if (type === 'start' || type === 'end') {
                return {
                  disabledHours: () =>
                    Array.from({ length: 24 }, (_, i) => i).filter(
                      (hour) => hour > now.hour(),
                    ),
                  disabledMinutes: (selectedHour: number) =>
                    selectedHour === now.hour()
                      ? Array.from({ length: 60 }, (_, i) => i).filter(
                          (minute) => minute > now.minute(),
                        )
                      : [],
                  disabledSeconds: (
                    selectedHour: number,
                    selectedMinute: number,
                  ) =>
                    selectedHour === now.hour() &&
                    selectedMinute === now.minute()
                      ? Array.from({ length: 60 }, (_, i) => i).filter(
                          (second) => second > now.second(),
                        )
                      : [],
                }
              }
              return {}
            }}
            showNow
          />
        )}
      </Flex>

      <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>
        Search
      </Button>
    </Flex>
  )
}

export default connect(select)(SearchBar)
